import React from 'react'
import { Sparkles, Copy, ExternalLink, X } from 'lucide-react'

// One-time credentials shown after "Create demo tenant" (moved from TenantsPage).
const copyToClipboard = (text) => {
  if (!text) return
  navigator.clipboard?.writeText(text).catch(() => {})
}

export const DemoTenantBanner = ({ demoSuccess, onDismiss }) => (
  <div className="card p-5 border-l-4 border-l-purple-500 bg-purple-50">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="p-2 bg-purple-100 rounded-lg shrink-0">
          <Sparkles className="h-5 w-5 text-purple-600" />
        </div>
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <h3 className="font-semibold text-admin-900">Estúdio de demonstração pronto</h3>
            {demoSuccess.demo?.created && (
              <p className="text-sm text-admin-700">
                Gerados {demoSuccess.demo.created.clients ?? 0} clientes,{' '}
                {demoSuccess.demo.created.whatsapp_chats ?? 0} conversas de WhatsApp,{' '}
                {demoSuccess.demo.created.whatsapp_messages ?? 0} mensagens,{' '}
                {demoSuccess.demo.created.photos ?? 0} fotos.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-admin-500 uppercase tracking-wide mb-1">
                URL do estúdio
              </label>
              <div className="flex items-center gap-1">
                <code className="flex-1 px-2 py-1.5 bg-white border border-admin-200 rounded text-xs text-admin-800 break-all">
                  {demoSuccess.studioUrl}
                </code>
                <button
                  type="button"
                  onClick={() => copyToClipboard(demoSuccess.studioUrl)}
                  className="p-1.5 text-admin-600 hover:text-admin-900 hover:bg-admin-100 rounded transition-colors"
                  title="Copiar URL"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <a
                  href={demoSuccess.studioUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 text-admin-600 hover:text-admin-900 hover:bg-admin-100 rounded transition-colors"
                  title="Abrir em nova aba"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            {demoSuccess.ownerCredentials?.email && (
              <div>
                <label className="block text-[11px] font-semibold text-admin-500 uppercase tracking-wide mb-1">
                  E-mail do proprietário
                </label>
                <div className="flex items-center gap-1">
                  <code className="flex-1 px-2 py-1.5 bg-white border border-admin-200 rounded text-xs text-admin-800 break-all">
                    {demoSuccess.ownerCredentials.email}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(demoSuccess.ownerCredentials.email)}
                    className="p-1.5 text-admin-600 hover:text-admin-900 hover:bg-admin-100 rounded transition-colors"
                    title="Copiar e-mail"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}

            {demoSuccess.ownerCredentials?.temporary_password && (
              <div>
                <label className="block text-[11px] font-semibold text-admin-500 uppercase tracking-wide mb-1">
                  Senha temporária
                </label>
                <div className="flex items-center gap-1">
                  <code className="flex-1 px-2 py-1.5 bg-white border border-admin-200 rounded text-xs text-admin-800 break-all">
                    {demoSuccess.ownerCredentials.temporary_password}
                  </code>
                  <button
                    type="button"
                    onClick={() =>
                      copyToClipboard(demoSuccess.ownerCredentials.temporary_password)
                    }
                    className="p-1.5 text-admin-600 hover:text-admin-900 hover:bg-admin-100 rounded transition-colors"
                    title="Copiar senha"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {demoSuccess.demo?.client_credentials && (
            <div className="rounded-lg border border-purple-200 bg-white/60 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-admin-900">
                  Acesso ao portal do cliente de demonstração
                </h4>
                <span className="text-[10px] uppercase tracking-wide text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                  senha compartilhada
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-admin-500 uppercase tracking-wide mb-1">
                    Senha (todos os clientes de demonstração)
                  </label>
                  <div className="flex items-center gap-1">
                    <code className="flex-1 px-2 py-1.5 bg-white border border-admin-200 rounded text-xs text-admin-800 break-all">
                      {demoSuccess.demo.client_credentials.password}
                    </code>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(demoSuccess.demo.client_credentials.password)
                      }
                      className="p-1.5 text-admin-600 hover:text-admin-900 hover:bg-admin-100 rounded transition-colors"
                      title="Copiar senha"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {demoSuccess.demo.client_credentials.sample_emails?.length > 0 && (
                  <div>
                    <label className="block text-[11px] font-semibold text-admin-500 uppercase tracking-wide mb-1">
                      E-mails de clientes de exemplo
                    </label>
                    <div className="space-y-1">
                      {demoSuccess.demo.client_credentials.sample_emails.map((email) => (
                        <div key={email} className="flex items-center gap-1">
                          <code className="flex-1 px-2 py-1.5 bg-white border border-admin-200 rounded text-xs text-admin-800 break-all">
                            {email}
                          </code>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(email)}
                            className="p-1.5 text-admin-600 hover:text-admin-900 hover:bg-admin-100 rounded transition-colors"
                            title="Copiar e-mail"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {demoSuccess.demo.client_credentials.note && (
                <p className="text-xs text-admin-600">
                  {demoSuccess.demo.client_credentials.note}
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-admin-600">
            Estas credenciais são exibidas apenas uma vez — copie-as agora se precisar entrar como
            proprietário ou como cliente de demonstração.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-1.5 text-admin-500 hover:text-admin-900 hover:bg-admin-100 rounded transition-colors shrink-0"
        title="Dispensar"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  </div>
)
